/**
 * MorningBriefPane — the "morning coffee" experience.
 *
 * Renders the full decision loop from INTENT.md:
 *   1. Portfolio delta (NAV, day change %, YTD vs S&P bps)
 *   2. Up to 5 decision cards with Approve / Defer / Debate
 *   3. "Approve all recommended" batch button
 *   4. Playbook status
 *   5. Deposit preview
 *   6. Hygiene highlights
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { usePending } from '../lib/usePending';
import { useToast } from '../lib/toast';
import { useAuth } from '../lib/auth';
import { useFocal } from '../lib/focal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useChatPanel } from '../App';
import { TicketsRail } from './TicketsRail';
import { PageHeader, PAGE_MAX_W } from './PageHeader';
import { SchwabConnectionBanner } from './SchwabConnectionBanner';
import { BulletinCards } from './BulletinCards';
import { Sparkline } from './viz/Sparkline';
import { TrendBadge } from './viz/TrendBadge';

export function MorningBriefPane() {
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useAuth();
  const focal = useFocal();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatPanel = useChatPanel();

  // Deeplink: `/today?chat=<message>` -> open chat with that seed message.
  // Used by the Debate button on each decision card and by external links
  // (e.g. from the morning email). v0.5.3 AE-040.
  useEffect(() => {
    const chatSeed = searchParams.get('chat');
    if (!chatSeed) return;
    chatPanel.openWithMessage(chatSeed);
    // Consume the param so back-navigation doesn't reopen the same chat.
    const next = new URLSearchParams(searchParams);
    next.delete('chat');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, chatPanel]);
  const isLive = me?.mode === 'live';
  const [brief, setBrief] = useState<api.MorningBriefFull | null>(null);
  const [mos, setMos] = useState<api.MosStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const { count: _pendingCount, refresh: refreshPending } = usePending();
  const [approving, setApproving] = useState<Set<string>>(new Set());
  const [decided, setDecided] = useState<Map<string, 'approved' | 'deferred' | 'rejected'>>(new Map());
  const [batchBusy, setBatchBusy] = useState(false);
  // v0.5.8 AE-104: 1-month equity sparkline behind the NAV headline.
  // Missing data renders nothing — the number stands alone, no fake trend.
  const [navCurve, setNavCurve] = useState<number[]>([]);

  const [alphaCurve, setAlphaCurve] = useState<number[]>([]);

  useEffect(() => {
    let alive = true;
    api.portfolio
      .pnl('1m')
      .then((p) => { if (alive) setNavCurve(p.points.map((pt) => pt.equity)); })
      .catch(() => { /* sparkline is an enhancement, never an error state */ });
    api.portfolio
      .alphaHistory(90)
      .then((a) => { if (alive && a.available) setAlphaCurve(a.points.map((pt) => pt.alpha_pct)); })
      .catch(() => { /* series accrues one row per reconcile; absence is normal early on */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await api.morningCoffee.mosStatus();
        if (!alive) return;
        setMos(m);
        const b = await api.morningBrief.fromMos(m);
        if (!alive) return;
        setBrief(b);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function approveOne(d: api.BriefDecision) {
    if (!d.client_order_id) return;
    setApproving((prev) => new Set(prev).add(d.id));
    try {
      await api.orders.approve(d.client_order_id, isLive);
      setDecided((prev) => new Map(prev).set(d.id, 'approved'));
      toast.show(`Approved: ${d.headline}`, 'ok');
      refreshPending();
    } catch (e) {
      toast.show(`Failed: ${e instanceof Error ? e.message : String(e)}`, 'bad');
    } finally {
      setApproving((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
    }
  }

  async function rejectOne(d: api.BriefDecision) {
    if (!d.client_order_id) return;
    setApproving((prev) => new Set(prev).add(d.id));
    try {
      await api.orders.reject(d.client_order_id);
      setDecided((prev) => new Map(prev).set(d.id, 'rejected'));
      toast.show(`Rejected: ${d.headline}`, 'warn');
      refreshPending();
    } catch (e) {
      toast.show(`Failed: ${e instanceof Error ? e.message : String(e)}`, 'bad');
    } finally {
      setApproving((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
    }
  }

  async function deferOne(d: api.BriefDecision) {
    if (!d.client_order_id) return;
    setApproving((prev) => new Set(prev).add(d.id));
    try {
      // v0.5.4 AE-071: server-side defer (push expiry by 24h). Survives
      // refresh, visible in Approvals queue, emits ORDER_DEFERRED ticket.
      await api.orders.defer(d.client_order_id, 24);
      setDecided((prev) => new Map(prev).set(d.id, 'deferred'));
      toast.show(`Deferred: ${d.headline} (24h)`, 'info');
      refreshPending();
    } catch (e) {
      toast.show(`Defer failed: ${e instanceof Error ? e.message : String(e)}`, 'bad');
    } finally {
      setApproving((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
    }
  }

  async function approveAll() {
    const pending = (brief?.decisions ?? []).filter(
      (d) => d.client_order_id && !decided.has(d.id),
    );
    if (pending.length === 0) return;
    setBatchBusy(true);
    try {
      // v0.5.4 AE-071: single batch-approve call. The server returns a
      // per-id result map so partial failures don't require client-side
      // reconciliation. PolicyEngine still fires per order on the server.
      const ids = pending.map((d) => d.client_order_id!);
      const res = await api.orders.batchApprove(ids, isLive);
      setDecided((prev) => {
        const next = new Map(prev);
        for (const d of pending) {
          const r = res.results[d.client_order_id!];
          if (r?.ok) next.set(d.id, 'approved');
        }
        return next;
      });
      refreshPending();
      if (res.fail === 0) {
        toast.show(`All ${res.ok} decisions approved`, 'ok');
      } else {
        toast.show(`Approved ${res.ok}, failed ${res.fail}`, 'warn');
      }
    } catch (e) {
      toast.show(`Batch approve failed: ${e instanceof Error ? e.message : String(e)}`, 'bad');
    } finally {
      setBatchBusy(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-ink-mute p-4">Loading Morning Brief...</div>;
  }

  if (!brief) {
    return <div className="text-sm text-ink-mute p-4">{err ?? 'Brief unavailable'}</div>;
  }

  const undecided = brief.decisions.filter((d) => !decided.has(d.id));

  const briefDate = brief.date ? new Date(brief.date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) : 'today';
  return (
    <div className={`space-y-4 ${PAGE_MAX_W} mx-auto`}>
      <PageHeader
        title="Morning Brief"
        subtitle={`${briefDate} — your morning coffee decisions. Review, approve, defer, or debate.`}
        badge={<SchwabConnectionBanner compact />}
      />
      {mos && <MosBanner mos={mos} />}

      {/* v0.5.6 AE-093: the agent-swarm bulletin (replaces the 6am email).
          Written by the orient timer; on-demand compose as fallback. */}
      <BulletinCards />

      {/* v0.5.4 AE-053 / AE-062: surface Schwab connection trouble loudly.
          The server uses BrokerReadStatus to tag the brief response. */}
      {brief.portfolio.source === 'schwab_unavailable' && (
        <div className="card p-3 border-warn bg-bg-elev" data-testid="schwab-unavailable-banner">
          <div className="text-sm font-semibold text-warn">Schwab data unavailable</div>
          <div className="text-[11px] text-ink-mute mt-0.5">
            Your account is connected but the read path failed
            {brief.portfolio.source_error ? `: ${brief.portfolio.source_error}` : '.'}{' '}
            Numbers below show "—" rather than synthetic values.{' '}
            <a href="/settings?tab=brokerage" className="underline">Reconnect in Settings</a>.
          </div>
        </div>
      )}

      {/* Portfolio delta */}
      <div className="card p-4" data-testid="brief-portfolio">
        <div className="text-xs text-ink-mute mb-1 flex items-center gap-2">
          <span>Portfolio</span>
          {/* v0.5.4 AE-064: live/paper provenance badge so the operator
              always knows which dataset they're looking at. */}
          {brief.portfolio.source === 'schwab' && (
            <span className="pill text-ok border-[#1f6a3a]" title="Numbers are from your real Schwab account.">live</span>
          )}
          {brief.portfolio.source === 'sim' && (
            <span className="pill text-ink-mute" title="Numbers are synthetic SimBroker data.">paper</span>
          )}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          {brief.portfolio.total_nav == null ? (
            <span className="num text-2xl font-bold text-ink-mute" title="NAV unavailable">—</span>
          ) : (
            <span className="num text-2xl font-bold">
              ${brief.portfolio.total_nav.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          )}
          {navCurve.length >= 2 && (
            <span className="self-center" data-testid="brief-nav-sparkline" title="1-month equity trend">
              <Sparkline values={navCurve} width={96} height={26} area />
            </span>
          )}
          {brief.portfolio.day_change_pct == null ? (
            <span className="num text-sm text-ink-mute" title="day change unavailable">day —</span>
          ) : (
            <span className="text-sm">
              day{' '}
              <TrendBadge
                delta={brief.portfolio.day_change_pct}
                label={`${brief.portfolio.day_change_pct >= 0 ? '+' : ''}${brief.portfolio.day_change_pct.toFixed(2)}%`}
                className="text-sm"
              />
            </span>
          )}
          {/* v0.5.7 AE-096 (UI truth sweep): when neither YTD number exists
              the row self-hides — a perpetual "YTD vs S&P —" is a widget
              that never resolves, not an honest miss. */}
          {brief.portfolio.ytd_vs_sp500_bps == null ? (
            brief.portfolio.ytd_return_pct == null ? null : (
              <span className={`num text-sm ${brief.portfolio.ytd_return_pct >= 0 ? 'text-ok' : 'text-bad'}`}>
                YTD {brief.portfolio.ytd_return_pct >= 0 ? '+' : ''}
                {brief.portfolio.ytd_return_pct.toFixed(2)}% (vs S&amp;P pending)
              </span>
            )
          ) : (
            <span className={`num text-sm ${brief.portfolio.ytd_vs_sp500_bps >= 0 ? 'text-ok' : 'text-bad'}`}>
              YTD vs S&amp;P {brief.portfolio.ytd_vs_sp500_bps >= 0 ? '+' : ''}
              {brief.portfolio.ytd_vs_sp500_bps}bps
            </span>
          )}
          {/* v0.5.8 AE-104: alpha trend accrued by reconcile; hides itself
              until >=2 real-data snapshots exist — no fake history. */}
          {alphaCurve.length >= 2 && (
            <span className="self-center" data-testid="brief-alpha-sparkline" title="Daily YTD-alpha vs S&P trend">
              <Sparkline values={alphaCurve} width={72} height={22} />
            </span>
          )}
        </div>
        {/* v0.5.5 dual-ledger: break the combined NAV into SIM / LIVE so
            the operator knows how much of "today's number" is paper. */}
        {(brief.portfolio.sim_nav != null || brief.portfolio.live_nav != null) && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-mute" data-testid="brief-ledger-breakdown">
            <span>
              LIVE{' '}
              <span className="num text-ink">
                {brief.portfolio.live_nav == null
                  ? '—'
                  : `$${brief.portfolio.live_nav.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              </span>
            </span>
            <span>
              SIM{' '}
              <span className="num text-ink">
                {brief.portfolio.sim_nav == null
                  ? '—'
                  : `$${brief.portfolio.sim_nav.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Decisions section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">
            Today's decisions ({brief.decisions.length})
          </h2>
          {undecided.length > 1 && (
            <button
              onClick={approveAll}
              disabled={batchBusy}
              className="btn-primary text-xs"
            >
              {batchBusy ? 'Approving...' : `Approve all ${undecided.length} recommended`}
            </button>
          )}
        </div>

        {brief.decisions.length === 0 ? (
          <div className="card p-4 text-sm text-ink-mute">
            No decisions today. You can let Meeri run.
          </div>
        ) : (
          <div className="space-y-3">
            {brief.decisions.map((d) => (
              <DecisionCard
                key={d.id}
                decision={d}
                status={decided.get(d.id)}
                busy={approving.has(d.id)}
                onApprove={() => approveOne(d)}
                onDefer={() => deferOne(d)}
                onDebate={() => navigate('/today?chat=' + encodeURIComponent(d.headline))}
                onReject={() => rejectOne(d)}
                onOpenThesis={(thesisId: string) => focal.set({ kind: 'thesis_detail', thesisId })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Playbook status */}
      <div className="card p-4">
        <div className="text-xs text-ink-mute mb-1">Playbook status</div>
        {brief.playbook_status.ok ? (
          <div className="text-sm text-ok font-medium">All gates green</div>
        ) : (
          <div>
            <div className="text-sm text-warn font-medium mb-1">Breaches detected</div>
            <ul className="text-xs text-ink-mute space-y-0.5">
              {brief.playbook_status.breaches.map((b, i) => (
                <li key={i}>- {b}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Deposit preview */}
      {brief.deposit_preview && (
        <div className="card p-4">
          <div className="text-xs text-ink-mute mb-1">Deposit flywheel</div>
          <div className="text-sm">
            Next detected: <span className="num font-semibold">${brief.deposit_preview.amount.toFixed(2)}</span>{' '}
            on {brief.deposit_preview.expected_date}
          </div>
          <div className="text-xs text-ink-mute mt-1">
            Proposed allocation: {brief.deposit_preview.allocation_summary}
          </div>
        </div>
      )}

      {/* Hygiene highlights */}
      {brief.hygiene_highlights.length > 0 && (
        <div className="card p-4">
          <div className="text-xs text-ink-mute mb-1">Hygiene highlights</div>
          <ul className="text-xs text-ink-mute space-y-0.5">
            {brief.hygiene_highlights.map((h, i) => (
              <li key={i}>- {h}</li>
            ))}
          </ul>
        </div>
      )}

      {err && <div className="card p-3 text-xs text-warn border-warn">{err}</div>}

      {/* Live floor activity (v0.5.3 AE-023): shows specialist tickets in real-time
          via SSE. Useful for operators who want to watch Meeri work mid-brief. */}
      <TicketsRail />
    </div>
  );
}

function MosBanner({ mos }: { mos: api.MosStatus }) {
  if (mos.is_halted) {
    const latest = mos.halts.filter((h) => !h.resolved_at).at(-1);
    return (
      <div className="card p-3 border-warn bg-bg-elev">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-warn text-sm">MEERI IS HALTED</div>
            <div className="text-xs text-ink-mute mt-0.5">{latest?.reason ?? 'reason unknown'}</div>
          </div>
          <span className="pill border-warn text-warn text-[10px]">degraded</span>
        </div>
      </div>
    );
  }
  if (mos.is_stale) {
    return (
      <div className="card p-3 border-edge">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">MOS is stale</div>
            <div className="text-xs text-ink-mute mt-0.5">{mos.digest}</div>
          </div>
          <span className="pill text-[10px]">stale</span>
        </div>
      </div>
    );
  }
  return (
    <div className="card p-3 border-edge">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">Good morning. {mos.digest}</div>
          <div className="text-xs text-ink-mute mt-0.5">
            current phase: <span className="num">{mos.current_phase ?? '—'}</span>
          </div>
        </div>
        <span className="pill text-[10px]">{mos.date}</span>
      </div>
    </div>
  );
}

function DecisionCard({
  decision: d,
  status,
  busy,
  onApprove,
  onDefer,
  onDebate,
  onReject,
  onOpenThesis,
}: {
  decision: api.BriefDecision;
  status?: 'approved' | 'deferred' | 'rejected';
  busy: boolean;
  onApprove: () => void;
  onDefer: () => void;
  onDebate: () => void;
  onReject: () => void;
  onOpenThesis?: (thesisId: string) => void;
}) {
  const sd = d as api.ServerBriefDecision;
  const thesisId = sd.thesis_id ?? null;
  const strategyId = sd.strategy_id ?? null;
  if (status) {
    return (
      <div className={`card p-4 ${
        status === 'approved' ? 'border-ok' : status === 'rejected' ? 'border-bad' : 'border-edge'
      }`}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{d.headline}</div>
          <span className={`pill text-[10px] ${
            status === 'approved' ? 'text-ok border-ok' : status === 'rejected' ? 'text-bad border-bad' : 'text-ink-mute'
          }`}>
            {status}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-sm font-semibold break-words">{d.headline}</div>
        {strategyId && (
          <span
            className="pill text-[10px] shrink-0"
            data-testid="decision-strategy-pill"
            title={`Proposed by the ${strategyId.replace(/_/g, ' ')} strategy`}
          >
            {strategyId.replace(/_/g, ' ')}
            {typeof sd.confidence === 'number' ? ` · ${Math.round(sd.confidence * 100)}%` : ''}
          </span>
        )}
      </div>
      <div className="text-xs text-ink-mute mb-2 break-words">{d.action}</div>
      <div className="text-xs text-ink mb-2 break-words" style={{ overflowWrap: 'anywhere' }}>{d.reasoning}</div>
      {d.contrarian_note && (
        <div className="border-l-2 border-warn pl-2 text-xs text-ink-mute italic mb-3 break-words" style={{ overflowWrap: 'anywhere' }}>
          <span className="text-warn not-italic text-[10px] uppercase font-semibold mr-1">Contrarian:</span>
          {d.contrarian_note}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={onApprove} disabled={busy} className="btn-primary text-xs">
          {busy ? 'Approving...' : 'Approve'}
        </button>
        <button onClick={onDefer} disabled={busy} className="btn text-xs">
          Defer
        </button>
        <button onClick={onDebate} disabled={busy} className="btn text-xs">
          Debate
        </button>
        <button onClick={onReject} disabled={busy} className="btn text-xs text-bad">
          Reject
        </button>
        {thesisId && onOpenThesis && (
          <button
            onClick={() => onOpenThesis(thesisId)}
            className="btn text-xs ml-auto"
            data-testid={`decision-open-thesis-${thesisId}`}
          >
            View thesis →
          </button>
        )}
      </div>
    </div>
  );
}
